/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export interface ColorPalette {
  darkMain: string;
  lightMain: string;
  darkSecondary: string;
  lightSecondary: string;
  background: string;
}

export interface LogoSet {
  navigation: string;
  hero: string;
  secondary: string;
  sponsor1: string;
  sponsor2: string;
}

export interface PageBanners {
  dashboard?: string;
  opportunities?: string;
  applications?: string;
  profile?: string;
  placements?: string;
  organizations?: string;
  analytics?: string;
  registry?: string;
  console?: string;
  advisees?: string;
  interviews?: string;
  advisors?: string;
  foundations?: string;
  messages?: string;
  settings?: string;
}

export interface PersonaSection {
  id: 'student' | 'employer' | 'parent' | 'advisor';
  label: string;
  title: string;
  description: string;
  image?: string;
  quote: string;
  author: string;
  authorInitials: string;
  authorDetails: string;
  items: string[];
}

export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  logo: string; // Lucide icon identifier or visual emoji representation
  primaryColor: string; // Tailwind hex color (e.g. #0284c7)
  secondaryColor: string; // Tailwind hex color (e.g. #0f172a)
  welcomeMessage: string;
  isActive?: boolean;
  isVisible?: boolean;
  highSchoolActive?: boolean;
  postSecondaryActive?: boolean;
  foundationsEnabled?: boolean;
  palette?: ColorPalette;
  logos?: LogoSet;
  heroImage?: string;
  pageBanners?: PageBanners;
  customDomain?: string;
  personaSections?: PersonaSection[];
  prohibitedEmailDomains?: string[];
}

export interface GlobalBranding {
  id: 'global';
  palette: ColorPalette;
  logos: LogoSet;
  heroVideo?: string;
  heroImage?: string;
  pageBanners?: PageBanners;
  clusterImages?: Record<string, string[]>;
  subclusterImages?: Record<string, string[]>;
  multiTenantEnabled?: boolean;
  customDomain?: string;
  prohibitedEmailDomains?: string[];
}

export type UserRole =
  | 'superadmin'
  | 'regional_admin'
  | 'advisor'
  | 'organization'
  | 'student'
  | 'parent';

export interface School {
  id: string;
  name: string;
  districtId: string;
  regionId: string;
  level?: string;
  category?: string;
}

export interface District {
  id: string;
  name: string;
  regionId: string;
}

export interface OrganizationTeamMember {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  title: string;
  email: string;
  phone: string;
  orgRole: 'owner' | 'admin' | 'supervisor' | 'contact_only';
  uid?: string;
  isLead?: boolean;
  addedAt: string;
  addedBy?: string;
}

export interface NotificationPreferences {
  // General & Channels
  standardAlerts: boolean;
  emailNotifications?: boolean;
  inAppNotifications?: boolean;
  instantEmailAlerts?: boolean;
  dailyDigest?: boolean;

  // Administrative & System Alert Categories (Especially Superadmin & Admin)
  ferpaConsentAlerts?: boolean;          // FERPA Releases & Parent Authorizations
  newUserRegistrationAlerts?: boolean;    // New User Registrations & Verifications
  partnerOrgAlerts?: boolean;            // Employer & Host Org Registrations / MOUs
  applicationAlerts?: boolean;           // Internship & Placement Applications
  hoursApprovalAlerts?: boolean;         // Student Hours Logged & Approvals
  evaluationAlerts?: boolean;            // Student Evaluations & Performance Reviews
  systemSecurityAlerts?: boolean;        // Security Events, Role Changes & Ledger Alerts
  opportunityCreatedAlerts?: boolean;    // New Opportunity Postings Published

  // Quiet Hours & Compliance
  nightlyBlackout: boolean;              // 10:00 PM – 6:00 AM Daily Silence
  schoolHourBlackout: boolean;           // 8:00 AM – 3:00 PM Weekday Silence
  complianceNode?: boolean;              // Compliance & Legal Notices
}

export interface UserProfile {
  uid: string;
  name: string;
  email: string;
  role: UserRole;
  tenantId: string; // Global superadmins may have a special tenantId or 'global'
  region?: string; // Needed for regional admins, students, and orgs
  childId?: string; // For parents: points to their active/primary student child's UID
  childrenIds?: string[]; // For parents: list of all linked student children UIDs
  parentId?: string; // For students: points to parent's UID
  orgName?: string; // For organization representatives
  parentOrgId?: string; // Umbrella primary organization UID when multiple contacts belong to one org
  orgTeamId?: string; // Shared team identifier for organization multi-user groups
  isOrgLead?: boolean; // True if this user holds the Lead title for the organization
  orgRole?: 'owner' | 'admin' | 'supervisor' | 'contact_only';
  orgTeamMembers?: OrganizationTeamMember[];
  orgAuthorizedEmails?: string[];
  avatar?: string; // URL or emoji-avatar placeholder
  photoURL?: string; // Firebase Auth sync URL
  profileComplete?: boolean;
  address?: string;
  school?: string; // Legacy name field
  schoolId?: string; // Unique school identifier
  pendingSchool?: string; // School name pending approval
  pendingSchoolId?: string; // Unique school identifier pending approval
  signupSchool?: string; // Requested school at signup
  signupSchoolId?: string; // Requested school ID at signup
  district?: string; // Legacy name field
  districtId?: string; // Unique district identifier
  contactFirstName?: string;
  contactLastName?: string;
  contactPhone?: string;
  professionalTitle?: string;
  industry?: string;
  website?: string;
  showInParticipatingOrgs?: boolean;
  description?: string;
  schoolsServed?: string[]; // Legacy name list
  schoolsServedIds?: string[]; // Unique school identifiers
  additionalSchoolIds?: string[]; // Additional schools/career centers associated (students)
  additionalSchools?: string[]; // Names of additional schools/career centers (students)
  companyProfileText?: string;
  onboardingText?: string;
  targetingMap?: string; // JSON or comma-separated list of selected skills
  extendedRegions?: string[]; // list of added or requested partner regions
  dateOfBirth?: string;
  createdAt?: string;
  lastLogin?: string;
  lastActive?: string;
  lastSignInTime?: string;
  classYear?: string;
  academicLevel?: string;
  ferpaConsent?: {
    granted: boolean;
    date: string;
    version: string;
    signatureType?: string;
    signatureValue?: string;
    studentAttestation?: {
      certified: boolean;
      timestamp: string;
      statement: string;
      emailProvided: string;
    };
    parentAttestation?: {
      certified: boolean;
      timestamp: string;
      statement: string;
      signerName: string;
      signerEmail: string;
      ipAddress?: string;
      userAgent?: string;
    };
  };
  ferpaConsentName?: string;
  ferpaConsentDate?: string;
  studentAttestation?: {
    certified: boolean;
    timestamp: string;
    statement: string;
    emailProvided: string;
  };
  parentAttestation?: {
    certified: boolean;
    timestamp: string;
    statement: string;
    signerName: string;
    signerEmail: string;
    ipAddress?: string;
    userAgent?: string;
  };
  parentEmail?: string; // Added for parent contact flow
  ferpaConsentSentAt?: string;
  ferpaConsentRecipientEmail?: string;
  regionCode?: string;
  studentCode?: string;

  // 18th Birthday Age of Majority FERPA Transition fields
  isAdult?: boolean;
  ferpa18TransitionProcessed?: boolean;
  ferpa18EmailSent?: boolean;
  ferpa18TransitionDate?: string;
  allowParentOversightAt18?: boolean;
  parentOversightPreferenceSet?: boolean;
  parentAccessRevokedAt18?: boolean;
  
  // For parents/delegates
  isDelegate?: boolean;
  studentChildId?: string;
  
  advisorVerified?: boolean;
  advisorVerifiedBy?: string;
  advisorVerificationDate?: string;
  advisorVerificationNotes?: string;
  advisorDenied?: boolean;
  advisorDeniedBy?: string;
  advisorDeniedDate?: string;
  advisorDeniedNotes?: string;
  verificationStatus?: 'verified' | 'denied' | 'pending';
  lastVerificationReminderSent?: string;
  verified?: boolean;
  verifiedBy?: string;
  verifiedAt?: string;
  verificationNotes?: string;
  resumeUrl?: string;
  resumeName?: string;
  firstLogin?: boolean;
  requiresPasswordChange?: boolean;
  lastPasswordReset?: string;
  resetByAdmin?: string;
  customPassword?: string;
  tempPassword?: string;
  
  // Settings & Compliance
  notificationPrefs?: NotificationPreferences;
  totalMinutesOnPortal?: number;
}

export type SlideType = 'text' | 'video' | 'image' | 'quiz';

export interface Slide {
  id: string;
  type: SlideType;
  title: string;
  content: string; // Markdown or main body
  secondaryContent?: string;
  layout?: 'full' | 'split' | 'media-left' | 'media-right';
  assetType?: 'none' | 'image' | 'video';
  assetUrl?: string;
  fontSizeHeader?: string; // 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' etc
  fontSizeBody?: string;
  containerSize?: 'focused' | 'extended';
  alignX?: 'left' | 'center' | 'right';
  alignY?: 'top' | 'center' | 'bottom';
  backgroundColor?: string;
  textColor?: string;
  minViewTime?: number;
  skippable?: boolean;
  quizType?: 'mcq' | 'matching';
  matchingPairs?: { left: string; right: string }[];
  quiz?: {
    question: string;
    options: string[];
    correctAnswer: string;
    correctIndex?: number;
  };
}

export interface Foundation {
  id: string;
  tenantId: string;
  title: string;
  description: string;
  pillar?: string; // e.g. 'Communication', 'Problem Solving', 'Leadership', 'Collaboration', 'Creativity', 'Integrity'
  heroImage?: string;
  slides: Slide[];
  createdAt: string;
}

export interface Opportunity {
  id: string;
  tenantId: string;
  organizationId: string | null;
  organizationName: string;
  title: string;
  description: string;
  requirements: string[];
  location: string;
  status: 'open' | 'closed' | 'archived';
  type: string; // allow any string or specific types ('internship' | 'co-op' | 'shadowing' | 'apprenticeship' etc)
  hoursRequired: number;
  createdAt: any;
  careerCluster?: string;
  subcluster?: string;
  occupation?: string;

  // Additional fields matching actual dataset document
  address?: string;
  applicationDeadline?: any;
  compensation?: string;
  createdBy?: string;
  customQuestions?: string[];
  externalContactEmail?: string;
  externalLogoUrl?: string;
  externalOrganizationName?: string;
  imageUrl?: string;
  hourlyRate?: string;
  industry?: string;
  isOpen?: boolean;
  isPublic?: boolean;
  jobFunction?: string;
  keyResponsibilities?: string;
  numOpenings?: number;
  paymentStructure?: string;
  qualifications?: string;
  regionId?: string;
  reportsTo?: string;
  semester?: string;
  term?: string;
  skills?: string;
  supervisorTitle?: string;
  targetEducationLevel?: string;
  targetRegionIds?: string[];
  targetSchoolDistricts?: string[];
  targetSchools?: string[];
  totalHours?: string | number;
  learningObjective?: string;
  minAge?: number;
  maxAge?: number;
  postedByUserId?: string;
  postedByUserEmail?: string;
  postedByName?: string;
  postedByRole?: string;
  isProxyPosting?: boolean;
  isExternalPartner?: boolean;
}

export interface Application {
  id: string;
  tenantId: string;
  opportunityId: string;
  opportunityTitle: string;
  organizationName: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  status: 'pending' | 'reviewed' | 'accepted' | 'rejected';
  appliedAt: string;
  recommendedBy?: string;
  decisionDate?: string;
  coverLetter?: string;
  resumeSummary?: string;
  interviewScheduled?: boolean;
  declineLetter?: string;
  postedByUserId?: string;
  postedByUserEmail?: string;
  postedByRole?: string;
  isProxyPosting?: boolean;
  externalContactEmail?: string;
}

export interface Interview {
  id: string;
  applicationId: string;
  opportunityId: string;
  opportunityTitle: string;
  tenantId: string;
  studentId: string;
  studentName: string;
  studentEmail?: string;
  organizationName: string;
  date: string;
  time: string;
  person: string;
  mode: 'virtual' | 'in_person' | 'phone';
  link?: string;
  address?: string;
  phone?: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  term?: string;
  orgNotes?: string;
  orgScore?: number;
}

export interface EvaluationScore {
  score: number;
  comments: string;
}

export interface TrainingAgreement {
  isAlignedToIGP?: boolean;
  isPaid: boolean;
  earnedCourseCredit?: boolean;
  courseCode?: string;
  hasInsurance?: boolean;
  jobDescription: string;
  learningObjectives: string[];
  startDate?: string;
  endDate?: string;
  assignedDays?: string[];
  careerPathway?: string;
  worksiteName?: string;
  assignedHoursTotal?: number;
  supervisorName?: string;
  supervisorPhone?: string;
  supervisorEmail?: string;
}

export interface PlacementEvaluation {
  status: 'pending' | 'released' | 'in_progress';
  scores: Record<string, EvaluationScore>;
  overallComments?: string;
  submittedAt?: string;
  supervisorSignature?: string;
  studentSignature?: string;
  advisorSignature?: string;
  vocationalSkillIds?: string[];
  dueDate?: string;
  reminderBeforeSent?: boolean;
  reminderAfterSent?: boolean;
}

export interface PlacementNote {
  id: string;
  text: string;
  createdAt: string; // ISO timestamp
  createdBy: {
    uid: string;
    name: string;
    role: string;
  };
  visibleToStudents: boolean;
  visibleToAdvisors: boolean;
}

export interface Placement {
  id: string;
  tenantId: string;
  opportunityId: string;
  opportunityTitle: string;
  organizationId: string;
  organizationName: string;
  studentId: string;
  studentName: string;
  studentEmail?: string;
  mentorId: string;
  mentorName: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'completed' | 'terminated' | 'hired';
  eval1?: PlacementEvaluation;
  eval2?: PlacementEvaluation;
  agreement?: TrainingAgreement;
  hiredDate?: string;
  assignedDays?: string[];
  assignedHoursTotal?: number;
  supervisorTitle?: string;
  registryContact?: string;
  directLine?: string;
  isRecommended?: boolean;
  isHiredByOrg?: boolean;
  notes?: PlacementNote[];
}

export interface StudentTask {
  id: string;
  tenantId: string;
  placementId: string;
  studentId: string;
  title: string;
  description: string;
  status: 'todo' | 'in_progress' | 'submitted' | 'approved';
  submissionNotes?: string;
  submissionLink?: string;
  feedback?: string;
  rating?: number; // 1-5 score
  dueDate: string;
  updatedAt: string;
}

export interface Occupation {
  id: string;
  name: string;
  subclusterIds: string[]; // Mapping to one or more subclusters
  description?: string;
  clusterId?: string; // For performance mapping back to cluster
}

export interface Cluster {
  id: string;
  name: string;
  description?: string;
}

export interface Subcluster {
  id: string;
  clusterId: string;
  name: string;
  description?: string;
}

export interface AttendanceReport {
  id: string;
  tenantId: string;
  placementId: string;
  studentId: string;
  studentName: string;
  weekEnding: string;
  hoursWorked: number;
  summary: string;
  status: 'pending' | 'approved' | 'rejected';
  feedback?: string;
  approvedBy?: string;
  createdAt: string;
}

export interface SystemLog {
  id: string;
  tenantId: string;
  userEmail: string;
  action: string;
  details: string;
  timestamp: string;
}

export interface PortalMessage {
  id: string;
  tenantId: string;
  toEmail: string;
  recipientName?: string;
  recipientRole?: string;
  subject: string;
  title: string;
  bodyHtml: string;
  sentAt: string;
  read: boolean;
  fromName?: string;
  deleted?: boolean;
  category?: string;
  channel?: 'email_and_portal' | 'email_only' | 'portal_only';
  deliveryStatus?: 'delivered' | 'simulated' | 'suppressed' | 'failed';
  suppressionReason?: string;
}

export interface Recommendation {
  id: string;
  tenantId: string;
  studentId: string;
  studentName: string;
  opportunityId: string;
  opportunityTitle: string;
  advisorId: string;
  advisorName: string;
  recommendedAt: string;
}

export const SUBCLUSTERS_MAP: Record<string, string[]> = {
  'Advanced Manufacturing': [
    'Engineering',
    'Industrial Machinery',
    'Production & Automation',
    'Robotics',
    'Safety & Quality Assurance'
  ],
  'Agriculture': [
    'Agribusiness',
    'Agricultural Technology & Automation',
    'Animal Systems',
    'Food Science & Processing',
    'Plant Systems',
    'Water Systems'
  ],
  'Art, Entertainment, & Design': [
    'Design & Digital Arts',
    'Fashion & Interiors',
    'Fine Arts',
    'Lighting & Sound Technology',
    'Media Production & Broadcasting',
    'Performing Arts'
  ],
  'Construction': [
    'Architecture & Civil Engineering',
    'Construction Planning & Development',
    'Equipment Operation & Maintenance',
    'Skilled Trades'
  ],
  'Digital Technology': [
    'Data Science & AI',
    'IT Support & Services',
    'Network Systems & Cybersecurity',
    'Software Solutions',
    'Unmanned Vehicle Technology',
    'Web & Cloud'
  ],
  'Education': [
    'Early Childhood Development',
    'Education Administration & Leadership',
    'Learner Support & Community Engagement',
    'Teaching, Training, & Facilitation'
  ],
  'Energy & Natural Resources': [
    'Clean & Alternative Energy',
    'Conservation & Land Management',
    'Ecological Research & Development',
    'Environmental Protection',
    'Resource Extraction',
    'Utilities'
  ],
  'Financial Services': [
    'Accounting',
    'Banking & Credit',
    'Financial Strategy & Investments',
    'Insurance',
    'Real Estate'
  ],
  'Health & Human Services': [
    'Behavioral & Mental Health',
    'Biotechnology Research & Development',
    'Community & Social Services',
    'Health Data & Administration',
    'Personal Care Services',
    'Physical Health'
  ],
  'Hospitality, Events, & Tourism': [
    'Accommodations',
    'Conferences & Events',
    'Culinary & Food Services',
    'Travel & Leisure'
  ],
  'Management & Entreprenuership': [
    'Business Information Management',
    'Entreprenuership & Small Business',
    'Leadership & Operations',
    'Project Management',
    'Regulation'
  ],
  'Marketing & Sales': [
    'Market Research, Analytics, & Ethics',
    'Marketing & Advertising',
    'Retail & Customer Experience',
    'Strategic Sales'
  ],
  'Public Service & Safety': [
    'Emergency Response',
    'Judicial Systems',
    'Local, State, & Federal Services',
    'Military & National Security',
    'Public Safety'
  ],
  'Supply Chain & Transportation': [
    'Air & Space Transportation',
    'Ground & Rail Transportation',
    'Maintenance & Repair',
    'Marine Transportation',
    'Planning & Logistics',
    'Purchasing & Warehousing'
  ]
};

export const CAREER_CLUSTERS = [
  {
    id: 'manufacturing',
    label: 'Advanced Manufacturing',
    skills: ['Engineering', 'Industrial Machinery', 'Production & Automation', 'Robotics', 'Safety & Quality Assurance']
  },
  {
    id: 'agriculture',
    label: 'Agriculture',
    skills: ['Agribusiness', 'Agricultural Technology & Automation', 'Animal Systems', 'Food Science & Processing', 'Plant Systems', 'Water Systems']
  },
  {
    id: 'art',
    label: 'Art, Entertainment, & Design',
    skills: ['Design & Digital Arts', 'Fashion & Interiors', 'Fine Arts', 'Lighting & Sound Technology', 'Media Production & Broadcasting', 'Performing Arts']
  },
  {
    id: 'construction',
    label: 'Construction',
    skills: ['Architecture & Civil Engineering', 'Construction Planning & Development', 'Equipment Operation & Maintenance', 'Skilled Trades']
  },
  {
    id: 'digital_tech',
    label: 'Digital Technology',
    skills: ['Data Science & AI', 'IT Support & Services', 'Network Systems & Cybersecurity', 'Software Solutions', 'Unmanned Vehicle Technology', 'Web & Cloud']
  },
  {
    id: 'education',
    label: 'Education',
    skills: ['Early Childhood Development', 'Education Administration & Leadership', 'Learner Support & Community Engagement', 'Teaching, Training, & Facilitation']
  },
  {
    id: 'energy',
    label: 'Energy & Natural Resources',
    skills: ['Clean & Alternative Energy', 'Conservation & Land Management', 'Ecological Research & Development', 'Environmental Protection', 'Resource Extraction', 'Utilities']
  },
  {
    id: 'financial',
    label: 'Financial Services',
    skills: ['Accounting', 'Banking & Credit', 'Financial Strategy & Investments', 'Insurance', 'Real Estate']
  },
  {
    id: 'health',
    label: 'Health & Human Services',
    skills: ['Behavioral & Mental Health', 'Biotechnology Research & Development', 'Community & Social Services', 'Health Data & Administration', 'Personal Care Services', 'Physical Health']
  },
  {
    id: 'hospitality',
    label: 'Hospitality, Events, & Tourism',
    skills: ['Accommodations', 'Conferences & Events', 'Culinary & Food Services', 'Travel & Leisure']
  },
  {
    id: 'management',
    label: 'Management & Entreprenuership',
    skills: ['Business Information Management', 'Entreprenuership & Small Business', 'Leadership & Operations', 'Project Management', 'Regulation']
  },
  {
    id: 'marketing',
    label: 'Marketing & Sales',
    skills: ['Market Research, Analytics, & Ethics', 'Marketing & Advertising', 'Retail & Customer Experience', 'Strategic Sales']
  },
  {
    id: 'public_service',
    label: 'Public Service & Safety',
    skills: ['Emergency Response', 'Judicial Systems', 'Local, State, & Federal Services', 'Military & National Security', 'Public Safety']
  },
  {
    id: 'supply_chain',
    label: 'Supply Chain & Transportation',
    skills: ['Air & Space Transportation', 'Ground & Rail Transportation', 'Maintenance & Repair', 'Marine Transportation', 'Planning & Logistics', 'Purchasing & Warehousing']
  }
];
